import React, { useState, useMemo } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReportVisualization = ({ selectedReport, dateRange, filters }) => {
  const [chartType, setChartType] = useState('bar');
  const [groupBy, setGroupBy] = useState('day');

  // Mock data generation based on report type
  const generateMockData = useMemo(() => {
    if (!selectedReport) return [];

    const { categoryId, reportId } = selectedReport;
    const dataPoints = groupBy === 'day' ? 30 : groupBy === 'week' ? 12 : 6;

    switch (categoryId) {
      case 'card-usage':
        return Array.from({ length: dataPoints }, (_, i) => ({
          period: groupBy === 'day' ? `Day ${i + 1}` : groupBy === 'week' ? `Week ${i + 1}` : `Month ${i + 1}`,
          activeCards: Math.floor(Math.random() * 500) + 200,
          newCards: Math.floor(Math.random() * 50) + 10,
          deactivatedCards: Math.floor(Math.random() * 20) + 5,
          usageCount: Math.floor(Math.random() * 1000) + 500,
          successRate: Math.floor(Math.random() * 20) + 80
        }));

      case 'payment-analytics':
        return Array.from({ length: dataPoints }, (_, i) => ({
          period: groupBy === 'day' ? `Day ${i + 1}` : groupBy === 'week' ? `Week ${i + 1}` : `Month ${i + 1}`,
          totalRevenue: Math.floor(Math.random() * 10000) + 5000,
          successfulPayments: Math.floor(Math.random() * 200) + 100,
          failedPayments: Math.floor(Math.random() * 20) + 5,
          averageAmount: Math.floor(Math.random() * 100) + 50,
          refunds: Math.floor(Math.random() * 1000) + 200
        }));

      case 'user-activity':
        return Array.from({ length: dataPoints }, (_, i) => ({
          period: groupBy === 'day' ? `Day ${i + 1}` : groupBy === 'week' ? `Week ${i + 1}` : `Month ${i + 1}`,
          activeUsers: Math.floor(Math.random() * 300) + 150,
          newUsers: Math.floor(Math.random() * 30) + 10,
          sessionDuration: Math.floor(Math.random() * 60) + 15,
          pageViews: Math.floor(Math.random() * 1000) + 500,
          bounceRate: Math.floor(Math.random() * 30) + 20
        }));

      case 'billing-summaries':
        return Array.from({ length: dataPoints }, (_, i) => ({
          period: groupBy === 'day' ? `Day ${i + 1}` : groupBy === 'week' ? `Week ${i + 1}` : `Month ${i + 1}`,
          totalBilled: Math.floor(Math.random() * 15000) + 8000,
          paidAmount: Math.floor(Math.random() * 12000) + 6000,
          outstandingAmount: Math.floor(Math.random() * 3000) + 1000,
          invoicesGenerated: Math.floor(Math.random() * 50) + 20,
          overdueInvoices: Math.floor(Math.random() * 10) + 2
        }));

      default:
        return [];
    }
  }, [selectedReport, groupBy]);

  const pieChartData = useMemo(() => {
    if (!selectedReport) return [];

    const { categoryId } = selectedReport;

    switch (categoryId) {
      case 'card-usage':
        return [
          { name: 'Active Cards', value: 450, color: '#10b981' },
          { name: 'Inactive Cards', value: 120, color: '#6b7280' },
          { name: 'Restricted Cards', value: 80, color: '#f59e0b' },
          { name: 'Expired Cards', value: 50, color: '#ef4444' }
        ];

      case 'payment-analytics':
        return [
          { name: 'Credit Card', value: 60, color: '#3b82f6' },
          { name: 'Bank Transfer', value: 25, color: '#10b981' },
          { name: 'Digital Wallet', value: 15, color: '#f59e0b' }
        ];

      case 'user-activity':
        return [
          { name: 'Individual Users', value: 320, color: '#8b5cf6' },
          { name: 'Team Users', value: 180, color: '#06b6d4' }
        ];

      case 'billing-summaries':
        return [
          { name: 'Paid', value: 75, color: '#10b981' },
          { name: 'Pending', value: 15, color: '#f59e0b' },
          { name: 'Overdue', value: 10, color: '#ef4444' }
        ];

      default:
        return [];
    }
  }, [selectedReport]);

  const chartTypes = [
    { type: 'bar', label: 'Bar Chart', icon: 'BarChart3' },
    { type: 'line', label: 'Line Chart', icon: 'TrendingUp' },
    { type: 'area', label: 'Area Chart', icon: 'Activity' },
    { type: 'pie', label: 'Pie Chart', icon: 'PieChart' }
  ];

  const groupByOptions = [
    { value: 'day', label: 'Daily' },
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' }
  ];

  const getChartKeys = () => {
    if (!selectedReport) return [];

    const { categoryId } = selectedReport;

    switch (categoryId) {
      case 'card-usage':
        return [
          { key: 'activeCards', color: '#10b981', name: 'Active Cards' },
          { key: 'newCards', color: '#3b82f6', name: 'New Cards' },
          { key: 'deactivatedCards', color: '#ef4444', name: 'Deactivated Cards' }
        ];

      case 'payment-analytics':
        return [
          { key: 'totalRevenue', color: '#10b981', name: 'Total Revenue' },
          { key: 'successfulPayments', color: '#3b82f6', name: 'Successful Payments' },
          { key: 'failedPayments', color: '#ef4444', name: 'Failed Payments' }
        ];

      case 'user-activity':
        return [
          { key: 'activeUsers', color: '#8b5cf6', name: 'Active Users' },
          { key: 'newUsers', color: '#06b6d4', name: 'New Users' }
        ];

      case 'billing-summaries':
        return [
          { key: 'totalBilled', color: '#3b82f6', name: 'Total Billed' },
          { key: 'paidAmount', color: '#10b981', name: 'Paid Amount' },
          { key: 'outstandingAmount', color: '#f59e0b', name: 'Outstanding Amount' }
        ];

      default:
        return [];
    }
  };

  const renderChart = () => {
    const chartKeys = getChartKeys();

    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={generateMockData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
              <XAxis dataKey="period" stroke="#a3a3a3" />
              <YAxis stroke="#a3a3a3" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#262626', 
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#ffffff'
                }} 
              />
              <Legend />
              {chartKeys.map(({ key, color, name }) => (
                <Bar key={key} dataKey={key} fill={color} name={name} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={generateMockData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
              <XAxis dataKey="period" stroke="#a3a3a3" />
              <YAxis stroke="#a3a3a3" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#262626', 
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#ffffff'
                }} 
              />
              <Legend />
              {chartKeys.map(({ key, color, name }) => (
                <Line key={key} type="monotone" dataKey={key} stroke={color} name={name} strokeWidth={2} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={generateMockData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
              <XAxis dataKey="period" stroke="#a3a3a3" />
              <YAxis stroke="#a3a3a3" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#262626', 
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#ffffff'
                }} 
              />
              <Legend />
              {chartKeys.map(({ key, color, name }) => (
                <Area key={key} type="monotone" dataKey={key} stackId="1" stroke={color} fill={color} fillOpacity={0.6} name={name} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={pieChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {pieChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#262626', 
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#ffffff'
                }} 
              />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  if (!selectedReport) {
    return (
      <div className="flex-1 flex items-center justify-center bg-background">
        <div className="text-center">
          <Icon name="BarChart3" size={64} className="text-text-muted mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">Select a Report</h3>
          <p className="text-text-muted">Choose a report from the left panel to view analytics and insights</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-background">
      {/* Chart Controls */}
      <div className="p-4 border-b border-border bg-surface">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-text-primary">Chart Type:</span>
              <div className="flex items-center space-x-1">
                {chartTypes.map(({ type, label, icon }) => (
                  <Button
                    key={type}
                    variant={chartType === type ? "primary" : "ghost"}
                    onClick={() => setChartType(type)}
                    className="p-2"
                    title={label}
                  >
                    <Icon name={icon} size={16} />
                  </Button>
                ))}
              </div>
            </div>

            {chartType !== 'pie' && (
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-text-primary">Group By:</span>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  className="px-3 py-1 bg-background border border-border rounded text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                >
                  {groupByOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Button variant="ghost" iconName="Maximize2" className="p-2" />
            <Button variant="ghost" iconName="Download" className="p-2" />
          </div>
        </div>
      </div>

      {/* Chart Area */}
      <div className="flex-1 p-6">
        <div className="bg-surface border border-border rounded-lg p-6">
          {renderChart()}
        </div>
      </div>

      {/* Chart Insights */}
      <div className="p-4 border-t border-border bg-surface">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary">
              {generateMockData.length > 0 ? generateMockData[generateMockData.length - 1][getChartKeys()[0]?.key] || 0 : 0}
            </div>
            <div className="text-sm text-text-muted">Latest Value</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-success">
              +{Math.floor(Math.random() * 20) + 5}%
            </div>
            <div className="text-sm text-text-muted">Growth Rate</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-text-primary">
              {Math.floor(Math.random() * 100) + 50}%
            </div>
            <div className="text-sm text-text-muted">Performance Score</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportVisualization;